// Utility functions for handling tab navigation in tutorial steps

import { SECTION_IDS, POSITIONING } from './tooltipConstants';

/**
 * Gets the patient ID from the currently open modal
 * @returns {string|null} - Patient ID or null if modal not found
 */
export const getPatientIdFromModal = () => {
  const modal = document.querySelector('.modal.show.modal-xl');
  if (!modal) return null;
  
  // Extract patient ID from modal ID (format: id{patient_id})
  const modalId = modal.id;
  return modalId.replace('id', '');
};

/**
 * Gets the next tab button ID for review_upload section steps
 * @param {string} stepId - Current step ID
 * @param {string} patientId - Patient ID
 * @returns {string|null} - Next tab button ID or null
 */
export const getNextTabId = (stepId, patientId) => {
  const tabMapping = {
    'review_note_page': `id${patientId}nav-diarized-tab`, // Transcript tab
    'transcript_page': `id${patientId}nav-citations-tab`, // Note Citations tab
    'note_citations': `id${patientId}nav-summary-tab`, // Patient Visit Summary tab
  };
  
  return tabMapping[stepId] || null;
};

/**
 * Gets the previous tab button ID for review_upload section steps
 * @param {string} stepId - Current step ID
 * @param {string} patientId - Patient ID
 * @returns {string|null} - Previous tab button ID or null
 */
export const getPreviousTabId = (stepId, patientId) => {
  const previousTabMapping = {
    'transcript_page': `id${patientId}nav-home-tab`, // Note tab
    'note_citations': `id${patientId}nav-diarized-tab`, // Transcript tab
    'patient_visit_summary_page': `id${patientId}nav-citations-tab`, // Note Citations tab
  };
  
  return previousTabMapping[stepId] || null;
};

/**
 * Clicks a tab button and executes a callback after delay
 * @param {string} tabId - Tab button ID
 * @param {Function} callback - Callback to execute after tab click
 * @returns {boolean} - True if tab was clicked, false otherwise
 */
export const clickTabAndCallback = (tabId, callback) => {
  const tabButton = document.querySelector(`#${tabId}`);
  if (!tabButton) return false;

  tabButton.click();
  setTimeout(() => {
    callback();
  }, POSITIONING.SCROLL_DELAY);

  return true;
};

/**
 * Handles next tab navigation for review_upload section
 * @param {string} sectionId - Current section ID
 * @param {string} stepId - Current step ID
 * @param {Function} nextStep - Function to move to next step
 * @returns {boolean} - True if tab navigation was handled, false otherwise
 */
export const handleNextTabNavigation = (sectionId, stepId, nextStep) => {
  if (sectionId !== SECTION_IDS.REVIEW_UPLOAD) return false;
  
  const patientId = getPatientIdFromModal();
  if (!patientId) return false;
  
  const nextTabId = getNextTabId(stepId, patientId);
  if (!nextTabId) return false;
  
  return clickTabAndCallback(nextTabId, nextStep);
};

/**
 * Handles previous tab navigation for review_upload section
 * @param {string} sectionId - Current section ID
 * @param {string} stepId - Current step ID
 * @param {Function} previousStep - Function to move to previous step
 * @returns {boolean} - True if tab navigation was handled, false otherwise
 */
export const handlePreviousTabNavigation = (sectionId, stepId, previousStep) => {
  if (sectionId !== SECTION_IDS.REVIEW_UPLOAD) return false;
  
  const patientId = getPatientIdFromModal();
  if (!patientId) return false;
  
  const previousTabId = getPreviousTabId(stepId, patientId);
  if (!previousTabId) return false;
  
  return clickTabAndCallback(previousTabId, previousStep);
};
